import { StackScreenProps } from "@react-navigation/stack";
import { ProductActionsRootParamList } from "../../../../types";
import { CreateVariantChildFormValues } from "../../components/CreateVariantChildForm/types";
import {
  useGenerateProductBarcode,
  useResetFormOnRootError,
  useSelectedProductEntityContextProvider,
} from "../../utils";
import { useActiveLocation } from "../../../../utilities/app-state/hooks";
import { CreateVariantChildForm } from "../../components/CreateVariantChildForm";
import { useCreateVariantChildFormProvider } from "../../components/CreateVariantChildForm/hooks";
import { useFormContext } from "react-hook-form";
import {
  Button,
  Page,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import React from "react";
import { Colors } from "react-native-ui-lib";

export type ProductActionsCreateChildVariantScreenProps = StackScreenProps<
  ProductActionsRootParamList,
  "ProductActionsCreateChildVariantScreen"
>;

const ProductActionsCreateChildVariant =
  ({}: ProductActionsCreateChildVariantScreenProps) => {
    const maybeActionsContext = useCreateVariantChildFormProvider();
    const { handleGenerateBarcode } = useGenerateProductBarcode();

    const formContext = useFormContext<CreateVariantChildFormValues>();
    const { handleSubmit, formState, setError, reset, watch } = formContext;

    const { isDirty, isLoading, isSubmitting, isValid, isSubmitSuccessful } =
      formState;

    useResetFormOnRootError({
      context: formContext,
    });

    const flashMessagesContext = useFlashMessages();

    const disabled =
      !isDirty ||
      isLoading ||
      isSubmitting ||
      isValid == false ||
      isSubmitSuccessful;

    const handleOnSubmit = React.useCallback(
      async (values: CreateVariantChildFormValues) => {
        try {
          await maybeActionsContext?.handleOnSubmit(values);
        } catch (err: any) {
          const msg = err?.message ?? "Something went wrong";
          flashMessagesContext.showMessage({
            message: msg,
            severity: "error",
          });
          setError("root", {
            type: "serverError",
            message: msg,
          });
        }
      },
      []
    );

    const footerMarkup = (
      <Button
        disabled={disabled}
        label="Duplicate Product"
        backgroundColor={Colors.action}
        color="white"
        onPress={handleSubmit(handleOnSubmit)}
      ></Button>
    );

    return (
      <Page title="Create variant child" footer={footerMarkup}>
        <CreateVariantChildForm
          onGenerateBarcodePress={handleGenerateBarcode}
        />
      </Page>
    );
  };

export const ProductActionsCreateChildVariantScreen = (
  props: ProductActionsCreateChildVariantScreenProps
) => {
  const { location } = useActiveLocation();

  const { product } = useSelectedProductEntityContextProvider();

  const defaultValues: Partial<CreateVariantChildFormValues> = {
    variantParentId: product.id,
    stockNumber: "",
    locationId: location?.id,
    selectedOptions: product.options.map((o) => {
      return {
        name: o.name,
        value: "",
      };
    }),
  };

  return (
    <CreateVariantChildForm.CreateVariantChildFormProvider
      defaultValues={defaultValues}
    >
      <ProductActionsCreateChildVariant {...props} />
    </CreateVariantChildForm.CreateVariantChildFormProvider>
  );
};
